import React from 'react';
import { motion } from 'motion/react';
import { Sparkles, Code2, Zap, Heart, Laptop } from 'lucide-react';
import { TOOLS } from '../../constants';

export default function About() {
  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="p-6 md:p-20 max-w-5xl mx-auto"
    >
      <div className="relative mb-20">
        <header className="relative z-10">
          <div className="flex items-center gap-3 text-accent mb-6">
            <Sparkles className="w-6 h-6 animate-pulse" />
            <span className="font-black uppercase tracking-[0.3em] text-xs">Our Mission</span>
          </div>
          <h1 className="text-6xl md:text-8xl font-black tracking-tighter dark:text-white leading-[0.9]">
            The <span className="text-accent">Aesthetic</span><br />
            Utility Engine.
          </h1>
          <p className="mt-10 text-xl text-slate-500 dark:text-slate-400 max-w-2xl leading-relaxed font-medium uppercase-none">
            QuicklyUtils was born from a simple obsession: why do technical tools have to look so boring? We combined high-performance algorithms with premium design to build the ultimate developer workspace.
          </p>
        </header>
        <div className="absolute -right-20 -top-20 w-96 h-96 bg-accent/10 rounded-full blur-[120px] pointer-events-none" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-24">
        {[
          { icon: Zap, title: "Pure Speed", desc: "No bloated frameworks. Just raw performance and instant results." },
          { icon: Code2, title: "DX First", desc: "Built by developers for developers. Clean inputs, formatted outputs." },
          { icon: Laptop, title: "Modern Stack", desc: "Powered by AI, React, and high-fidelity motion graphics." }
        ].map((feature, i) => (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            key={feature.title} 
            className="p-8 bg-white dark:bg-slate-900 rounded-[2.5rem] border border-line dark:border-slate-800"
          >
            <div className="w-12 h-12 bg-slate-50 dark:bg-slate-800 rounded-2xl flex items-center justify-center text-accent mb-6">
              <feature.icon className="w-6 h-6" />
            </div>
            <h3 className="text-xl font-bold mb-2 dark:text-white">{feature.title}</h3>
            <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed uppercase-none">{feature.desc}</p>
          </motion.div>
        ))}
      </div>

      <div className="bg-slate-900 rounded-[3rem] p-12 md:p-20 relative overflow-hidden">
        <div className="relative z-10 flex flex-col md:flex-row gap-12 items-center">
          <div className="flex-1 text-center md:text-left">
            <h2 className="text-4xl font-black text-white mb-6 tracking-tight italic">Engineered for everyone.</h2>
            <p className="text-slate-400 mb-8 max-w-md mx-auto md:mx-0 text-sm leading-relaxed uppercase-none">
              Whether you are a senior architect or just starting your coding journey, QuicklyUtils provides the precision you need without the friction you hate.
            </p>
            <div className="flex flex-wrap justify-center md:justify-start gap-4">
              <div className="px-6 py-3 bg-white/10 rounded-2xl border border-white/10 text-white font-bold text-xs">
                {TOOLS.length} Active Engines
              </div>
              <div className="px-6 py-3 bg-white/10 rounded-2xl border border-white/10 text-white font-bold text-xs">
                Global Edge Cache
              </div>
            </div>
          </div>
          <div className="flex-1 flex justify-center">
            <div className="relative">
              <div className="w-48 h-48 bg-accent rounded-[3rem] rotate-12 flex items-center justify-center shadow-2xl">
                <Sparkles className="w-20 h-20 text-white" />
              </div>
              <div className="absolute -top-6 -right-6 w-24 h-24 bg-white/10 rounded-[2rem] -rotate-6 backdrop-blur-sm border border-white/20 flex items-center justify-center">
                 <Heart className="w-8 h-8 text-rose-400 fill-rose-400" />
              </div>
            </div>
          </div>
        </div>
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-accent/20 rounded-full blur-[150px] -mr-64 -mt-64" />
      </div>

      <footer className="mt-24 text-center">
        <p className="text-slate-300 dark:text-slate-700 text-[11px] font-black uppercase tracking-[0.4em]">Design & Engineering by <a href="https://allysonbelo.com" target="_blank" rel="noreferrer" className="text-accent hover:underline">Allyson Belo</a></p>
      </footer>
    </motion.div>
  );
}
